// CommonButtons.tsx

import {
  ActivityIndicator,
  Platform,
  Pressable,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  View,
} from "react-native"
import BFText, { H5, H6 } from "@/components/standard/BFText"
import React, { useRef } from "react"
import { type GestureResponderEvent } from "react-native/Libraries/Types/CoreEventTypes"
import { Path, Svg, type SvgProps } from "react-native-svg"
import { cn } from "@/components/util/cn"
import { Horizontal } from "@/components/standard/BFLayout"
import { createComponentClickedEvent, track } from "@/analytics/events/base"
import { useScreenName } from "@/hooks/useScreenName"
import { type PressableRef } from "@rn-primitives/types"

type ButtonSize = "lg" | "md" | "sm"
type ButtonVariant =
  | "primary"
  | "secondary"
  | "primaryDark"
  | "secondaryDark"
  | "specialty"
  | "underlined-action"

function LightSelectionIcon({ size }: { size: ButtonSize }) {
  return (
    <Svg
      width={size === "sm" ? 12 : 16}
      height={size === "sm" ? 12 : 16}
      viewBox="0 0 17 16"
      fill="none"
    >
      <Path
        d="M8.5 0a8 8 0 108 8 8.008 8.008 0 00-8-8zm3.512 6.59l-4.307 4.307a.616.616 0 01-.871 0L4.988 9.05a.616.616 0 01.87-.871L7.27 9.592l3.873-3.874a.616.616 0 01.87.871z"
        fill="#75FAD4"
      />
    </Svg>
  )
}

function DarkSelectionIcon({ size }: { size: ButtonSize }) {
  return (
    <Svg
      width={size === "sm" ? 12 : 16}
      height={size === "sm" ? 12 : 16}
      viewBox="0 0 17 16"
      fill="none"
    >
      <Path
        d="M8.5 0a8 8 0 108 8 8.008 8.008 0 00-8-8zm3.512 6.59l-4.307 4.307a.616.616 0 01-.871 0L4.988 9.05a.616.616 0 01.87-.871L7.27 9.592l3.873-3.874a.616.616 0 01.87.871z"
        fill="#121F3A"
      />
    </Svg>
  )
}

export interface CommonButtonProps extends PressableProps {
  title: string
  size?: ButtonSize
  selected?: boolean
  noSelectedIcon?: boolean
  variant?: ButtonVariant
  textStyle?: StyleProp<TextStyle>
  textClassName?: string
  /**
   * This can be used to ensure a button only calls onPress once. Note this
   * should only be used if the button is used to navigate away and maybe save
   * so pressing more than once is harmful. It lasts until the button doesn't
   * render anymore.
   */
  pressOnceOnly?: boolean
  /**
   * Loading style added, this also prevents clicking again.
   */
  loading?: boolean
  /** Force testID on our buttons */
  testID: string
  /** Icon to display when selected */
  icon?: React.FC<SvgProps>
  /** For performing an alternate action when all requirements are not met */
  pseudoDisabled?: boolean
  onPseudoPress?: () => void // <== Alternative action
}

const pressableVariants: Record<
  ButtonVariant,
  (props: {
    pressed?: boolean
    selected?: boolean
    disabled?: boolean | null
    pseudoDisabled?: boolean | null
  }) => string
> = {
  primary: ({ disabled, pseudoDisabled, selected }) =>
    disabled || pseudoDisabled
      ? "bg-light-grey"
      : selected
        ? "bg-[#144460]"
        : "bg-calm-blue hover:bg-[#1B5A7F] active:bg-[#144460]",
  primaryDark: ({ disabled, selected }) =>
    disabled
      ? "bg-light-grey"
      : selected
        ? "bg-teal"
        : "bg-teal hover:bg-[#68DCBB] active:bg-[#5AC0A3]",
  secondary: ({ disabled, selected, pseudoDisabled }) =>
    disabled || pseudoDisabled
      ? "bg-light-grey border-light-grey border-1"
      : selected
        ? "bg-calm-blue border border-midnight-blue hover:bg-[#1B5A7F] active:bg-[#144460]"
        : "bg-off-white border border-midnight-blue hover:bg-[#EEF1F4] active:bg-[#D3D5D8]",
  secondaryDark: ({ disabled, selected }) =>
    disabled
      ? "bg-light-grey"
      : selected
        ? "bg-teal hover:bg-[#5AC0A3]"
        : "bg-off-white hover:bg-[#EEF1F4] active:bg-[#D3D5D8]",
  specialty: ({ disabled, selected }) =>
    disabled
      ? "bg-light-grey border border-midnight-blue px-4"
      : selected
        ? "bg-[#244075] border border-midnight-blue"
        : "bg-lightest border border-midnight-blue hover:bg-[#EEF1F4] active:bg-[#D3D5D8] px-4",
  "underlined-action": ({ disabled }) =>
    disabled ? "bg-transparent opacity-25" : "bg-transparent",
}

const textVariants: Record<
  ButtonVariant,
  (props: {
    pressed?: boolean
    selected?: boolean
    disabled?: boolean | null
    pseudoDisabled?: boolean | null
  }) => string
> = {
  primary: ({ disabled, pseudoDisabled }) =>
    disabled || pseudoDisabled ? "text-warm-grey" : "text-teal",
  primaryDark: ({ disabled, pseudoDisabled }) =>
    disabled || pseudoDisabled ? "text-warm-grey" : "text-midnight-blue",
  secondary: ({ disabled, selected, pseudoDisabled }) =>
    disabled || pseudoDisabled
      ? "text-warm-grey"
      : selected
        ? "text-teal"
        : "text-midnight-blue",
  secondaryDark: ({ disabled }) =>
    disabled ? "text-warm-grey" : "text-midnight-blue",
  specialty: () => "text-midnight-blue",
  "underlined-action": ({ disabled }) =>
    disabled ? "text-warm-grey" : "text-calm-blue",
}

export const BFButton = ({
  title,
  size = "lg",
  selected,
  noSelectedIcon,
  disabled,
  className,
  textStyle,
  textClassName,
  pressOnceOnly = false,
  loading = false,
  variant = "primary",
  icon: Icon,
  onPress,
  pseudoDisabled = false,
  onPseudoPress,
  testID,
  ...rest
}: CommonButtonProps) => {
  const currentScreen = useScreenName()
  const pressedOnce = useRef(false)

  function handlePress(event: GestureResponderEvent) {
    track(
      createComponentClickedEvent({
        current_screen: currentScreen,
        component_id: testID,
        component_type: "button",
        component_function: `pressed: ${title}`,
        action_taken: pseudoDisabled
          ? "disabled button pressed"
          : "button pressed",
        component_text: title,
      })
    )

    if (!onPress || loading) {
      return
    }

    if (pseudoDisabled) {
      console.log(`"${title}" button is pseudoDisabled`)
      if (onPseudoPress) {
        onPseudoPress() // Trigger alternative action
      }
      return
    }

    if (pressOnceOnly) {
      if (pressedOnce.current) {
        console.log(`User pressed "${title}" button more than once`)
        return
      }
      pressedOnce.current = true
    }

    onPress(event)
  }

  const ButtonText =
    variant === "underlined-action"
      ? () => (
          <BFText
            className={cn(
              "text-calm-blue text-[14px] underline",
              textClassName
            )}
          >
            {title}
          </BFText>
        )
      : size === "sm"
        ? H6
        : H5

  // Silly padding because NativeWind doesn't yet support outline for native, need to shave
  // off the border from padding (p-4 == 16px, p-2 === 8px).. breaks if spacing changes.
  const sizeClassName = `${variant === "secondary" ? (size === "sm" ? "p-[7px]" : "p-[15px]") : size === "sm" ? "p-2" : "p-4"} ${size === "md" ? "max-w-[250px]" : ""}`

  const containerClass =
    variant !== "underlined-action" ? "w-full rounded-full" : ""

  // Constants for alignment and spacing
  const alignmentClass =
    variant !== "underlined-action" ? "justify-center items-center" : ""

  const gapClass = variant === "specialty" ? "gap-1" : "gap-2"

  const customIconShouldDisplay =
    (selected && !noSelectedIcon && !loading && !disabled) ||
    variant === "specialty"

  const DefaultSelectedIcon =
    variant === "secondary" || variant === "primary"
      ? LightSelectionIcon
      : DarkSelectionIcon

  return (
    <Pressable
      {...rest}
      testID={testID}
      // @ts-expect-error dataSet is present but not in types
      dataSet={{ disabled, loading }}
      onPress={onPress && handlePress}
      disabled={disabled}
      className={cn(
        containerClass,
        sizeClassName,
        pressableVariants[variant]?.({
          selected,
          disabled: disabled || loading,
          pseudoDisabled,
          pressed: false,
        }),
        className
      )}
    >
      {({ pressed }) => (
        <Horizontal className={cn(alignmentClass, gapClass)}>
          <ButtonText
            selectable={false}
            className={cn(
              size === "sm" && "leading-[14px]",
              textVariants[variant]?.({
                selected,
                disabled: disabled || loading,
                pseudoDisabled,
              })
            )}
          >
            {title}
          </ButtonText>
          {/* Only display the icon if icon is set and button selected */}
          {customIconShouldDisplay && (
            <View
              style={{
                marginLeft: 0,
                paddingRight: Platform.OS === "web" ? 12 : 0,
              }}
            >
              {Icon ? (
                <Icon width={16} height={16} />
              ) : (
                <DefaultSelectedIcon size={size} />
              )}
            </View>
          )}
          {loading && (
            <ActivityIndicator size={"small"} style={{ maxHeight: 10 }} />
          )}
        </Horizontal>
      )}
    </Pressable>
  )
}

type NoVariantInButtonProps = Omit<CommonButtonProps, "variant">

export const PrimaryButton: React.FC<NoVariantInButtonProps> = (
  props: NoVariantInButtonProps
) => <BFButton variant="primary" {...props} />

export const PrimaryDarkButton: React.FC<NoVariantInButtonProps> = (
  props: NoVariantInButtonProps
) => <BFButton variant="primaryDark" {...props} />

export const SecondaryButton: React.FC<NoVariantInButtonProps> = (
  props: NoVariantInButtonProps
) => {
  return <BFButton variant="secondary" {...props} />
}

export const SecondaryLightButton: React.FC<NoVariantInButtonProps> = (
  props: NoVariantInButtonProps
) => {
  return <BFButton variant="secondary" {...props} />
}

export const SecondaryDarkButton: React.FC<NoVariantInButtonProps> = (
  props: NoVariantInButtonProps
) => {
  return <BFButton variant="secondaryDark" {...props} />
}

export const SpecialtyButton: React.FC<NoVariantInButtonProps> = (
  props: NoVariantInButtonProps
) => <BFButton variant="specialty" size="sm" {...props} />

export const UnderlinedActionButton: React.FC<NoVariantInButtonProps> = (
  props: NoVariantInButtonProps
) => <BFButton variant="underlined-action" size="sm" {...props} />

type BFPressableChildrenProps =
  | Pick<PressableProps, "children">
  | ((state: {
      pressed: boolean
      hovered: boolean
      loading?: boolean
      disabled?: boolean | null
      title: string
    }) => React.ReactNode)
export interface BFPressableProps extends Omit<PressableProps, "children"> {
  title: string
  /**
   * This can be used to ensure a button only calls onPress once. Note this
   * should only be used if the button is used to navigate away and maybe save
   * so pressing more than once is harmful. It lasts until the button doesn't
   * render anymore.
   */
  pressOnceOnly?: boolean
  /**
   * Loading style added, this also prevents clicking again.
   */
  loading?: boolean
  /** Force testID on our buttons */
  testID: string
  /** For performing an alternate action when all requirements are not met */
  pseudoDisabled?: boolean
  onPseudoPress?: () => void // <== Alternative action
  children?: BFPressableChildrenProps
}

export const BFPressable = React.forwardRef<PressableRef, BFPressableProps>(
  (
    {
      title,
      disabled,
      pressOnceOnly = false,
      onPress,
      pseudoDisabled = false,
      onPseudoPress,
      testID,
      loading,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const currentScreen = useScreenName()
    const pressedOnce = useRef(false)

    function handlePress(event: GestureResponderEvent) {
      track(
        createComponentClickedEvent({
          current_screen: currentScreen,
          component_id: testID,
          component_type: "button",
          component_function: `pressed: ${title}`,
          action_taken: pseudoDisabled
            ? "disabled button pressed"
            : "button pressed",
          component_text: title,
        })
      )

      if (!onPress || loading) {
        return
      }

      if (pseudoDisabled) {
        console.log(`"${title}" pressable is pseudoDisabled`)
        if (onPseudoPress) {
          onPseudoPress() // Trigger alternative action
        }
        return
      }

      if (pressOnceOnly) {
        if (pressedOnce.current) {
          console.log(`User pressed "${title}" pressable more than once`)
          return
        }
        pressedOnce.current = true
      }

      onPress(event)
    }

    return (
      <Pressable
        testID={testID}
        // @ts-expect-error dataSet is present but not in types
        dataSet={{ disabled, loading }}
        onPress={onPress && handlePress}
        disabled={disabled}
        className={cn(className)}
        ref={ref}
        {...props}
      >
        {({ pressed, hovered }) => {
          if (!children || typeof children !== "function") {
            return null
          }

          return children({ pressed, hovered, loading, disabled, title })
        }}
      </Pressable>
    )
  }
)
